// SpotlightTour.tsx - Main spotlight tour component with overlay
import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useSpotlight } from '../../hooks/useSpotlight';
import TourStep from './TourStep';

interface SpotlightTourProps {
  className?: string;
}

const SpotlightTour: React.FC<SpotlightTourProps> = ({ className }) => {
  const {
    isTourActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    nextStep,
    prevStep,
    skipTour,
    endTour,
  } = useOnboarding();

  const {
    targetRect,
    tooltipPosition,
    targetElement,
    scrollToTarget,
  } = useSpotlight({
    targetSelector: currentStep?.targetSelector || '',
    placement: currentStep?.placement || 'auto',
    padding: currentStep?.spotlightPadding ?? 8,
  });

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isTourActive) return;

    switch (e.key) {
      case 'Escape':
        skipTour();
        break;
      case 'ArrowRight':
      case 'Enter':
        nextStep();
        break;
      case 'ArrowLeft':
        prevStep();
        break;
    }
  }, [isTourActive, skipTour, nextStep, prevStep]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Call onBeforeStep callback when step changes
  useEffect(() => {
    if (isTourActive && currentStep?.onBeforeStep) {
      currentStep.onBeforeStep();
    }
  }, [isTourActive, currentStepIndex, currentStep]);

  // Scroll target into view when step changes
  useEffect(() => {
    if (isTourActive && targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const isInViewport =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth;

      if (!isInViewport) {
        scrollToTarget();
      }
    }
  }, [isTourActive, targetElement, currentStepIndex, scrollToTarget]);

  // Add/remove spotlight class on target element and its card container
  useEffect(() => {
    if (isTourActive && targetElement) {
      targetElement.classList.add('spotlight-target');

      // For nested elements (like buttons inside cards), find and elevate the card container
      // The card container is identified by having a data-tour-step attribute
      let cardContainer: HTMLElement | null = null;
      let parent = targetElement.parentElement;

      while (parent && !cardContainer) {
        if (parent.hasAttribute('data-tour-step')) {
          cardContainer = parent;
        }
        parent = parent.parentElement;
      }

      // Only elevate the card container if found (for nested targets like buttons)
      if (cardContainer) {
        cardContainer.classList.add('spotlight-ancestor');
      }

      return () => {
        targetElement.classList.remove('spotlight-target');
        if (cardContainer) {
          cardContainer.classList.remove('spotlight-ancestor');
        }
      };
    }
  }, [isTourActive, targetElement]);

  // Prevent body scroll when tour is active
  useEffect(() => {
    if (isTourActive) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isTourActive]);

  if (!isTourActive || !currentStep) {
    return null;
  }

  const isLastStep = currentStepIndex === totalSteps - 1;
  const isFirstStep = currentStepIndex === 0;

  // Create SVG mask for spotlight effect
  const renderOverlay = () => {
    // If target not found, show full dark overlay
    if (!targetRect) {
      return (
        <div
          className="fixed inset-0 bg-black/75 transition-all duration-300"
          style={{ zIndex: 10000 }}
        />
      );
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    return (
      <svg
        className="fixed inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 10000 }}
      >
        <defs>
          <mask id="spotlight-mask">
            {/* White background = visible */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {/* Black rectangle = transparent hole */}
            <rect
              x={targetRect.left}
              y={targetRect.top}
              width={targetRect.width}
              height={targetRect.height}
              rx="8"
              ry="8"
              fill="black"
            />
          </mask>
        </defs>
        {/* Dark overlay with mask */}
        <rect
          x="0"
          y="0"
          width={viewportWidth}
          height={viewportHeight}
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
          className="transition-all duration-300"
        />
      </svg>
    );
  };

  // Clickable overlay to skip tour
  const renderClickableOverlay = () => (
    <div
      className="fixed inset-0 cursor-pointer"
      style={{ zIndex: 9999 }}
      onClick={skipTour}
      aria-label="Click to skip tour"
    />
  );

  const content = (
    <>
      {/* Clickable backdrop */}
      {renderClickableOverlay()}

      {/* SVG spotlight overlay */}
      {renderOverlay()}

      {/* Tooltip */}
      {tooltipPosition ? (
        <TourStep
          step={currentStep}
          position={tooltipPosition}
          currentIndex={currentStepIndex}
          totalSteps={totalSteps}
          onNext={nextStep}
          onPrev={prevStep}
          onSkip={skipTour}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
        />
      ) : (
        // Fallback: centered tooltip when target not found
        <TourStep
          step={currentStep}
          position={{
            top: window.innerHeight / 2 - 120,
            left: window.innerWidth / 2 - (currentStep.width || 320) / 2,
            placement: 'bottom',
            arrowPosition: 50,
          }}
          currentIndex={currentStepIndex}
          totalSteps={totalSteps}
          onNext={nextStep}
          onPrev={prevStep}
          onSkip={skipTour}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
        />
      )}
    </>
  );

  return createPortal(content, document.body);
};

export default SpotlightTour;
